import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import subscription from "../../Asset/subscription.svg";
import fillcheck from "../../Asset/fill check.svg";
import { ReactSVG } from "react-svg";
import { BiSolidFilePdf } from "react-icons/bi";
import { HiDotsVertical } from "react-icons/hi";
import { useSelector } from "react-redux";
import { selectUser } from "../../Redux/authSlice";
import { toast } from "react-toastify";
import ButtonLoading from "../../component/ButtonLoading";
import { useGetSubscriptionQuery } from "../../Redux/stripeApi";
import Sidebar from "../../component/sidebar/sidebar";
import hamburgerIcon from "../../Asset/Hamburger.svg";
import { trackEvent } from "../../lib/mixpanel";

const normalizeBillingPeriod = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (["annual", "yearly", "year"].includes(normalized)) return "annual";
  if (["monthly", "month"].includes(normalized)) return "monthly";
  return "";
};

const ManageSubscription = () => {
  const [openSidebar, setOpenSidebar] = useState(true);
  const [openSidebarMobile, setOpenSidebarMobile] = useState(false);

  const [searchParams] = useSearchParams();
  const success = searchParams.get("success");

  const features = [
    "Unlimited words per week",
    "Custom prompt creation",
    "Unlimited audio transcription per week",
    "Early access to new features",
  ];

  const { data, isLoading: getSubLoading, refetch: refetchSubscription } = useGetSubscriptionQuery(undefined, { refetchOnMountOrArgChange: true });
  const subscriptionData = data?.existingSub;

  const user = useSelector(selectUser);
  const navigate = useNavigate();

  const [isAnnual, setIsAnnual] = useState(false);
  const [customerId, setCustomerId] = useState(null);

  // Billing history
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invError, setInvError] = useState("");

  // Card info
  const [card, setCard] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardError, setCardError] = useState("");

  const [menuOpenId, setMenuOpenId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Base URL once
  const API_BASE = useMemo(
    () =>
      (process.env.REACT_APP_BACKEND_URL || "http://localhost:5050").replace(
        /\/$/,
        ""
      ),
    []
  );

  const email = useMemo(
    () => (user?.email || user?.EmailId || "").trim(),
    [user]
  );

  // ---- Derived Subscription UI + Status (NEW DB rules) ----
  // SubscriptionType: "Pro" | "Free"
  // SubscriptionPeriod: "Monthly" | "Annual"
  const {
    isPro,
    currentPeriod,
    currentPeriodNormalized,
    expiresAt,
    isActive,
    planLabel,
  } = useMemo(() => {
    if (!subscriptionData) {
      return {
        isPro: false,
        currentPeriod: null,
        currentPeriodNormalized: "",
        expiresAt: null,
        isExpired: true,
        isActive: false,
        planLabel: "ZT Basic",
      };
    }

    const type = String(subscriptionData.SubscriptionType || "").trim(); // Pro | Free
    const period = String(subscriptionData.SubscriptionPeriod || "").trim(); // Monthly | Annual
    const currentPeriodNormalized = normalizeBillingPeriod(period);

    const isPro = type === "Pro";

    const expiresAt = subscriptionData.EndDate
      ? new Date(subscriptionData.EndDate)
      : null;

    const now = new Date();
    const isExpired = !expiresAt || isNaN(+expiresAt) ? true : now >= expiresAt;

    // active only when Pro + not expired
    const isActive = isPro && !isExpired;

    const planLabel = isPro
      ? currentPeriodNormalized === "annual"
        ? "ZT Pro Annual"
        : "ZT Pro"
      : "ZT Basic";

    return {
      isPro,
      currentPeriod: period || null,
      currentPeriodNormalized,
      expiresAt,
      isExpired,
      isActive,
      planLabel,
    };
  }, [subscriptionData]);

  // ---- Button enable/disable logic (IMPORTANT FIX) ----
  // Allow monthly->annual upgrade even if user is active
  const desiredPeriod = isAnnual ? "Annual" : "Monthly";
  const desiredPeriodNormalized = isAnnual ? "annual" : "monthly";

  // Disable only if they are already active on the SAME period
  const isSamePeriodActive =
    isActive &&
    currentPeriodNormalized &&
    currentPeriodNormalized === desiredPeriodNormalized;

  // If backend blocks downgrade, you can also disable downgrade from UI:
  const isDowngradeAttempt = isActive && currentPeriodNormalized === "annual" && !isAnnual;

  const disableUpgradeButton =
    getSubLoading || isLoading || isSamePeriodActive || isDowngradeAttempt;

  // close invoice menu on outside click
  useEffect(() => {
    const onDocClick = () => setMenuOpenId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  // Ensure server session is refreshed when /settings loads.
  useEffect(() => {
    const ctrl = new AbortController();

    (async () => {
      try {
        await fetch(`${API_BASE}/api/auth/refresh-session`, {
          method: "POST",
          credentials: "include",
          signal: ctrl.signal,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("[refresh-session] error:", err);
        }
      }
    })();

    return () => ctrl.abort();
  }, [API_BASE]);

  // Resolve customer id when email is ready
  useEffect(() => {
    if (!email) return;
    const ctrl = new AbortController();

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stripe/get-customer-id`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
          signal: ctrl.signal,
        });

        const json = await res.json();
        if (!res.ok) throw new Error(json?.error || "Failed to get customer id");

        setCustomerId(json.customer_id || null);
        console.log("[ManageSubscription] customer_id =", json.customer_id, email);
      } catch (err) {
        if (err.name !== "AbortError")
          console.error("[get-customer-id] error:", err);
      }
    })();

    return () => ctrl.abort();
  }, [API_BASE, email]);

  // Fetch payment method
  useEffect(() => {
    if (!customerId) return;
    const ctrl = new AbortController();
    setCardLoading(true);
    setCardError("");

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stripe/payment-method`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customerId }),
          signal: ctrl.signal,
        });

        const json = await res.json();
        if (!res.ok)
          throw new Error(json?.error || "Failed to get payment method");

        setCard(json.payment_method || null);
      } catch (e) {
        if (e.name !== "AbortError") setCardError(e.message);
      } finally {
        setCardLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [API_BASE, customerId]);

  // Fetch invoices
  useEffect(() => {
    if (!customerId) return;

    const ctrl = new AbortController();
    setInvLoading(true);
    setInvError("");
    setInvoices([]);

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stripe/invoices`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customerId, limit: 100, status: "paid" }),
          signal: ctrl.signal,
        });

        const ct = res.headers.get("content-type") || "";
        if (!ct.includes("application/json")) {
          const text = await res.text();
          throw new Error(
            `Non-JSON response (${res.status}): ${text.slice(0, 100)}`
          );
        }

        const json = await res.json();
        if (!res.ok) throw new Error(json?.error || "Failed to fetch invoices");

        const mapped = (json.invoices || []).map((inv) => {
          const hosted = inv.link ?? inv.hosted_invoice_url ?? null;
          const pdf = inv.pdf ?? inv.invoice_pdf ?? null;
          const viewUrl = hosted || pdf || null;

          return {
            id: inv.id,
            invoiceNo: inv.invoiceNo || inv.id,
            viewUrl,
            pdf,
            status: inv.status,
            amount: ((inv.amount_due ?? inv.amountDue) || 0) / 100,
            currency: (inv.currency || "").toUpperCase(),
            paidDate: new Date((inv.created || 0) * 1000).toLocaleDateString(),
          };
        });

        setInvoices(mapped);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("[invoices] error:", err);
          setInvError(err.message);
        }
      } finally {
        setInvLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [API_BASE, customerId]);

  // Subscription
  const handleSubscription = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    // Safety: prevent buying same period again
    if (isSamePeriodActive) {
      toast.info(
        `You already have an active ${
          desiredPeriod === "Annual" ? "Annual" : "Monthly"
        } subscription.`
      );
      return;
    }

    // Optional: block downgrades in UI (if your backend blocks it too)
    if (isDowngradeAttempt) {
      toast.info("You’re already on Annual. Downgrades are not supported here.");
      return;
    }

    setIsLoading(true);

    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}api/stripe/create-subscription-session`;

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email,
          name: `${user?.FirstName ?? ""} ${user?.LastName ?? ""}`.trim(),
          // Backend expects this
          plan: isAnnual ? "Pro_Annual" : "Pro",
        }),
      });

      let payload = null;
      try {
        payload = await res.json();
      } catch {}

      if (!res.ok) {
        const msg =
          payload?.message ||
          payload?.error ||
          res.statusText ||
          "Failed to create subscription session.";
        toast.error(msg);
        // If the backend found an active Stripe subscription and synced it to the DB,
        // refetch so the UI immediately reflects the real subscription state.
        if (payload?.alreadyActive) refetchSubscription();
        return;
      }

      if (payload?.success && payload?.clientSecret) {
        navigate(
          `/checkout/${isAnnual ? "N9sJ0qWz7eB1" : "aJ3d8Lk29vX4"}/${
            payload.clientSecret
          }`
        );
        return;
      }

      toast.error(
        payload?.message || payload?.error || "Unexpected server response."
      );
    } catch (err) {
      toast.error(err?.message || "Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const truncateText = (str, maxLength = 30) => {
    if (!str) return "";
    return str.length > maxLength ? str.substring(0, maxLength) + "..." : str;
  };

  useEffect(() => {
    if (success) {
      switch (success) {
        case "user_login":
          toast.success("Login successful! Welcome back");
          break;
        default:
          toast.success("Action completed successfully!");
      }
    }
  }, [success]);

  return (
    <>
      <Sidebar
        openSidebar={openSidebar}
        setOpenSidebar={setOpenSidebar}
        openSidebarMobile={openSidebarMobile}
        setOpenSidebarMobile={setOpenSidebarMobile}
      />

      <div
        className={`w-full bg-white flex flex-col text-black overflow-hidden transition-all duration-500 ${
          openSidebar ? "lg:pl-[200px] xl:pl-[260px]" : "lg:pl-16"
        }`}
      >
        <div className="h-[88px] flex items-center xl:px-10 lg:px-9 md:px-8 px-5 border-b border-[#0000001A]">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setOpenSidebarMobile(true)}
              className="lg:hidden inline-flex items-center justify-center rounded-lg border border-[#0000001A] p-2 text-[#374151]"
              aria-label="Open sidebar"
            >
              <img src={hamburgerIcon} alt="" aria-hidden="true" className="size-5" />
            </button>

            {/* <NavLink to="/" className="inline-block" aria-label="Zero Typing — Home">
              <span className="xl:text-[1.875rem] lg:text-[1.7rem] md:text-[1.5rem] text-xl font-bold font-jakarta flex gap-3 items-center">
                <HiArrowLeft className="xl:size-8 lg:size-7 md:size-6 size-5 text-[#33333370]" />
                Manage Subscription
              </span>
            </NavLink> */}
          </div>
        </div>

        <div className="xl:p-20 lg:p-14 md:p-10 p-6 flex flex-col xl:gap-10 lg:gap-8 gap-6 w-full max-w-screen-2xl mx-auto">
        <div className="flex lg:flex-row flex-col md:items-stretch w-full md:gap-10 gap-5">
          <div className="md:flex-1 w-full border border-[#0000001A] bg-[#FAFAFA] rounded-xl md:p-5 p-3 flex flex-col md:gap-7 gap-5">
            <div className="flex justify-between items-center gap-2">
              <span className="xl:text-[1.875rem] lg:text-[1.5rem] md:text-[1.3rem] text-xl font-semibold font-inter">
                Current Plan
              </span>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href =
                    "https://billing.stripe.com/p/login/test_6oEaHM7cfdJJ9xK4gg";
                }}
                className="xl:text-[1.125rem] lg:text-base text-sm underline font-semibold font-inter text-[#00000066] cursor-pointer"
              >
                Cancel Plan
              </button>
            </div>

            <div className="flex flex-col gap-1">
              <span className="xl:text-[1.294rem] lg:text-[1.125rem] capitalize text-base font-inter font-medium text-[#1A1A1A66]">
                {planLabel}
              </span>
              <span className="font-inter font-semibold xl:text-[1.563rem] lg:text-[1.294rem] md:text-[1.125rem] text-base">
                {isActive ? "Paid" : "Free"}
              </span>
              {expiresAt && !isNaN(+expiresAt) && (
                <span className="text-sm font-inter text-[#00000066] mt-1">
                  {!isPro
                    ? "Your free minutes reset on"
                    : isActive
                    ? "Renews on"
                    : "Expired on"}{" "}
                  {expiresAt.toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              )}
            </div>
          </div>

          <div className="md:flex-1 w-full border border-[#0000001A] bg-[#FAFAFA] rounded-xl md:p-5 p-3 flex flex-col md:gap-7 gap-5">
            <div className="flex justify-between items-center gap-2">
              <h1 className="xl:text-[1.875rem] lg:text-[1.5rem] md:text-[1.3rem] text-xl font-semibold font-inter">
                Billing Details
              </h1>
              <NavLink
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href =
                    "https://billing.stripe.com/p/login/test_6oEaHM7cfdJJ9xK4gg";
                }}
                className="xl:text-[1.125rem] lg:text-base text-sm underline font-semibold font-inter text-[#00000066] cursor-pointer"
              >
                Change Payment Method
              </NavLink>
            </div>

            <div className="flex gap-6 items-start">
              <div>
                <h1 className="xl:text-[1.375rem] lg:text-xl md:text-lg text-base font-semibold font-inter text-[#000]">
                  Card Number
                </h1>
                <span className="xl:text-[1.25rem] lg:text-lg text-base font-medium font-inter text-[#00000066]">
                  {cardLoading
                    ? "Loading…"
                    : card?.last4
                    ? `**** **** **** ${card.last4}  (${(
                        card.brand || ""
                      ).toUpperCase()})`
                    : "—"}
                </span>
                {cardError && (
                  <div className="text-sm text-red-600">{cardError}</div>
                )}
              </div>

              {!!card?.exp_month && !!card?.exp_year && (
                <div>
                  <h1 className="xl:text-[1.375rem] lg:text-xl md:text-lg text-base font-semibold font-inter text-[#000]">
                    Expiry
                  </h1>
                  <span className="xl:text-[1.25rem] lg:text-lg text-base font-medium font-inter text-[#00000066]">
                    {String(card.exp_month).padStart(2, "0")}/
                    {String(card.exp_year).slice(-2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Subscribe */}
        <div className="w-full border border-[#0000001A] bg-[#FAFAFA] rounded-xl">
          <div className="flex items-center justify-between py-4 md:px-6 px-4">
            <div className="xl:text-[1.875rem] lg:text-[1.5rem] md:text-[1.3rem] text-xl font-semibold font-inter">
              Subscribe
            </div>

            <button
              onClick={() => {
                trackEvent("upgrade_clicked", {
                  current_period: currentPeriod || "none",
                  target_period: isAnnual ? "Annual" : "Monthly",
                  is_active_subscription: isActive,
                });
                handleSubscription();
              }}
              disabled={disableUpgradeButton}
              className={`bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#3AA6E4] hover:to-[#4175EB] transition-all duration-300 ease-in-out lg:py-3 py-2 flex items-center justify-center ${
                disableUpgradeButton ? "opacity-50 cursor-not-allowed" : ""
              } px-4 md:rounded-xl rounded-lg font-inter font-semibold lg:text-[1.125rem] md:text-base text-sm text-white`}
            >
              {isLoading ? (
                <ButtonLoading text="Upgrading..." />
              ) : isSamePeriodActive ? (
                "Current Plan"
              ) : isDowngradeAttempt ? (
                "Downgrade not available"
              ) : currentPeriodNormalized === "monthly" && isAnnual ? (
                "Upgrade to Annual"
              ) : (
                "Upgrade Plan"
              )}
            </button>
          </div>

          <div className="border-b border-[#0000001A] w-full" />

          <div className="py-4 md:px-6 px-4 flex flex-col gap-6">
            <div className="flex gap-3 items-center xl:text-[1.563rem] lg:text-[1.294rem] md:text-[1.125rem] text-base font-inter font-semibold">
              <ReactSVG
                src={subscription}
                beforeInjection={(svg) => {
                  svg.setAttribute("class", "w-auto xl:h-12 lg:h-10 h-9");
                }}
                className="w-auto"
              />
              <span>ZT Pro</span>
            </div>

            <div className="flex gap-2 md:flex-row flex-col items-start pb-8">
              <div className="md:w-1/3 w-full xl:text-[1.375rem] lg:text-xl md:text-lg text-base font-inter font-medium">
                Get all premium ZT features. Everything in basic plus:
              </div>

              <div className="md:w-2/3 w-full grid md:grid-cols-2 grid-cols-1 xl:gap-4 gap-3">
                {features.map((feature, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-[#00000099] xl:text-[1.119rem] lg:text-base md:text-sm font-inter font-medium"
                  >
                    <ReactSVG
                      src={fillcheck}
                      beforeInjection={(svg) => {
                        svg.setAttribute("class", "xl:w-6 w-5 xl:h-6 h-5");
                      }}
                    />
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="py-4 md:px-6 px-4 border-t border-[#0000001A]">
            <div className="flex items-center gap-8">
              <div className="flex md:flex-row flex-col md:items-center gap-4">
                <h2 className="text-[2.33rem] leading-none font-semibold font-inter">
                  ${isAnnual ? "144" : "15"}{" "}
                  <span className="text-[0.935rem] font-inter">
                    {isAnnual ? "/year" : "/month"}
                  </span>
                </h2>

                <div className="flex justify-center w-max">
                  <div className="bg-white p-2 md:rounded-2xl rounded-lg flex gap-1 w-full text-[0.994rem] font-semibold font-inter">
                    <button
                      onClick={() => setIsAnnual(false)}
                      className={`flex-1 md:py-3 py-2 md:px-12 px-7 md:rounded-2xl rounded-lg text-sm font-medium transition ${
                        !isAnnual
                          ? "bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#3AA6E4] hover:to-[#4175EB] transition-all duration-300 ease-in-out text-white"
                          : "text-gray-700"
                      }`}
                    >
                      Monthly
                    </button>

                    <button
                      onClick={() => setIsAnnual(true)}
                      className={`flex-1 md:py-3 py-2 md:px-12 px-7 md:rounded-2xl rounded-lg text-sm font-medium transition ${
                        isAnnual
                          ? "bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#3AA6E4] hover:to-[#4175EB] transition-all duration-300 ease-in-out text-white"
                          : "text-gray-700"
                      }`}
                    >
                      Annual
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Optional helper text */}
            {isDowngradeAttempt && (
              <div className="mt-3 text-sm text-[#00000066]">
                You’re currently on Annual. Please use the Billing Portal to change plans.
              </div>
            )}
          </div>
        </div>

        {/* Billing History */}
        <div className="flex flex-col md:gap-4 gap-3">
          <h1 className="xl:text-[1.875rem] lg:text-[1.5rem] md:text-[1.3rem] text-xl font-inter font-semibold">
            Billing History
          </h1>

          {invLoading && (
            <div className="bg-[#FAFAFA] p-4 rounded-xl text-[1.125rem] text-[#00000066]">
              Loading invoices…
            </div>
          )}

          {invError && (
            <div className="bg-[#FFF3F3] border border-[#ffcccc] p-4 rounded-xl text-[1.0rem] text-[#b30000]">
              {invError}
            </div>
          )}

          <div className="flex flex-col gap-2">
            {!invLoading &&
              !invError &&
              invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex justify-between items-center bg-[#FAFAFA] lg:p-4 md:p-3 p-2 mf:rounded-xl rounded-md font-medium font-inter text-[1.25rem]"
                >
                  <div className="flex gap-3 items-center">
                    <BiSolidFilePdf className="text-[#E5252A] xl:size-8 lg:size-7 md:size-6 size-5" />
                    {invoice.viewUrl ? (
                      <a
                        href={invoice.viewUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="underline lg:text-base md:text-sm text-xs"
                        title="Open invoice"
                      >
                        {truncateText(invoice.invoiceNo, 15)}
                      </a>
                    ) : (
                      <span className="lg:text-base md:text-sm text-xs">
                        {truncateText(invoice.invoiceNo, 15)}
                      </span>
                    )}
                  </div>

                  <div className="xl:text-[1.0rem] lg:text-sm md:text-xs text-[10px] text-[#00000099]">
                    {invoice.paidDate}
                  </div>

                  <div className="flex items-center gap-3 relative">
                    <button
                      type="button"
                      aria-haspopup="menu"
                      aria-expanded={menuOpenId === invoice.id}
                      aria-label="Invoice actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(
                          menuOpenId === invoice.id ? null : invoice.id
                        );
                      }}
                      className="p-1 rounded-md hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black/20"
                    >
                      <HiDotsVertical className="lg:size-8 md:size-6 size-5 text-[#00000026]" />
                    </button>

                    {menuOpenId === invoice.id && (
                      <div
                        role="menu"
                        className="absolute right-0 top-10 z-20 min-w-44 rounded-xl bg-white shadow-lg ring-1 ring-black/5 p-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {invoice.pdf ? (
                          <a
                            href={invoice.pdf}
                            target="_blank"
                            rel="noreferrer"
                            role="menuitem"
                            className="block w-full rounded-lg px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100"
                          >
                            Download PDF
                          </a>
                        ) : (
                          <div
                            role="menuitem"
                            className="block w-full rounded-lg px-3 py-2 text-left text-sm text-gray-400 cursor-not-allowed"
                            title="No PDF available"
                          >
                            Download PDF
                          </div>
                        )}

                        {invoice.viewUrl ? (
                          <a
                            href={invoice.viewUrl}
                            target="_blank"
                            rel="noreferrer"
                            role="menuitem"
                            className="block w-full rounded-lg px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100"
                          >
                            View Online
                          </a>
                        ) : (
                          <div
                            role="menuitem"
                            className="block w-full rounded-lg px-3 py-2 text-left text-sm text-gray-400 cursor-not-allowed"
                            title="No Link Available"
                          >
                            View Online
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}

            {!invLoading && !invError && invoices.length === 0 && (
              <div className="bg-[#FAFAFA] p-4 rounded-xl text-[1.125rem] text-[#00000066]">
                No invoices yet.
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default ManageSubscription;
